import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import CustomTooltip from "../../../_metronic/layout/components/common/CustomTooltip";
import {
  createAdminAnnouncement,
  deleteAdminAnnouncement,
  getAdminAnnouncements,
  updateAdminAnnouncement,
} from "../../../api";
import { charValidate } from "../../../components/utility";
import { snackActions } from "../../../helpers/SnackUtilsConfigurator";

export interface AnnouncementData {
  id: number;
  title: string;
  description: string;
  createdOn?: string;
}

interface TableOptions {
  skip: number;
  take: number;
  totalRowCount: number;
  SearchText: string;
}

interface UseAdminAnnouncementsReturn {
  announcements: AnnouncementData[];
  tableOptions: TableOptions;
  tableData: AnnouncementData[];
  columns: GridColDef[];
  selectedRow: AnnouncementData | null;
  isUpdateMode: boolean;
  showAddEditModal: boolean;
  showDeleteConfirmModal: boolean;
  showViewModal: boolean;
  viewData: AnnouncementData | null;
  loading: boolean;
  isCreatingAnnouncement: boolean;
  isDeletingAnnouncement: boolean;
  setSelectedRow: (row: AnnouncementData | null) => void;
  setShowAddEditModal: (show: boolean) => void;
  setShowDeleteConfirmModal: (show: boolean) => void;
  setShowViewModal: (show: boolean) => void;
  setViewData: (data: AnnouncementData | null) => void;
  handlePagination: (values: { page: number; pageSize: number }) => void;
  handleLastPage: () => void;
  handleFirstPage: () => void;
  handleModalClose: () => void;
  handleCloseDelete: () => void;
  handleViewClose: () => void;
  handleAddClick: () => void;
  handleSaveAnnouncement: (values: {
    title: string;
    description: string;
  }) => Promise<boolean>;
  handleDeleteAnnouncement: () => Promise<void>;
}

export const useAdminAnnouncements = (): UseAdminAnnouncementsReturn => {
  const [announcements, setAnnouncements] = useState<AnnouncementData[]>([]);
  const [tableOptions, setTableOptions] = useState<TableOptions>({
    skip: 0,
    take: 10,
    totalRowCount: 0,
    SearchText: "",
  });
  const [selectedRow, setSelectedRow] = useState<AnnouncementData | null>(null);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewData, setViewData] = useState<AnnouncementData | null>(null);

  const { mutateAsync: getAnnouncementData, isLoading: isGettingListData } =
    useMutation("get-admin-announcements", getAdminAnnouncements);

  const { mutateAsync: createAnnounce, isLoading: isCreatingAnnouncement } =
    useMutation("create-admin-announcement", createAdminAnnouncement);

  const { mutateAsync: updateAnnouncement } = useMutation(
    "update-admin-announcement",
    updateAdminAnnouncement,
  );

  const { mutateAsync: deleteAnnouncement, isLoading: isDeletingAnnouncement } =
    useMutation("delete-admin-announcement", deleteAdminAnnouncement);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const data = await getAnnouncementData();
        setAnnouncements(data?.result || []);
        setTableOptions((prev) => ({
          ...prev,
          totalRowCount: data?.result?.length || 0,
        }));
      } catch (error) {
        snackActions.error("Failed to fetch announcements");
      }
    };
    fetchAnnouncements();
  }, [getAnnouncementData]);

  const tableData = useMemo(() => {
    let filteredData = [...announcements];

    if (tableOptions.SearchText) {
      const searchLower = tableOptions.SearchText.toLowerCase();
      filteredData = filteredData.filter(
        (item) =>
          item.title.toLowerCase().includes(searchLower) ||
          item.description.toLowerCase().includes(searchLower),
      );
    }

    const skip = tableOptions.skip;
    const take = tableOptions.take;
    return filteredData.slice(skip, skip + take);
  }, [
    announcements,
    tableOptions.skip,
    tableOptions.take,
    tableOptions.SearchText,
  ]);

  const columns: GridColDef[] = [
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      sortable: false,
      minWidth: 200,
      renderCell: (data: any) => (
        <CustomTooltip
          title={
            data?.row ? (
              <h6 style={{ color: "white" }}>{data?.row?.title}</h6>
            ) : (
              "N/A"
            )
          }
        >
          <span>{data?.row?.title}</span>
        </CustomTooltip>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      sortable: false,
      minWidth: 300,
      renderCell: (data: any) => (
        <CustomTooltip
          title={
            data?.row?.description ? (
              <h6 style={{ color: "white" }}>{data?.row?.description}</h6>
            ) : (
              "N/A"
            )
          }
        >
          <span>
            {data?.row?.description
              ? charValidate(data?.row?.description, 50)
              : "N/A"}
          </span>
        </CustomTooltip>
      ),
    },
    {
      field: "createdOn",
      headerName: "Created On",
      flex: 0.5,
      sortable: false,
      renderCell: (data: any) =>
        data?.row?.createdOn ? `${data?.row?.createdOn}` : "N/A",
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (data: any) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title="View">
            <VisibilityIcon
              onClick={() => {
                setViewData(data?.row);
                setShowViewModal(true);
              }}
              sx={{ fontSize: 22, cursor: "pointer", color: "#6c757d" }}
            />
          </Tooltip>
          <Tooltip title="Re-Notify">
            <NotificationsActiveIcon
              onClick={() => {
                setIsUpdateMode(false);
                setSelectedRow(data?.row);
                setShowAddEditModal(true);
              }}
              sx={{ fontSize: 22, cursor: "pointer", color: "#1976d2" }}
            />
          </Tooltip>
          <Tooltip title="Update">
            <DriveFileRenameOutlineIcon
              onClick={() => {
                setIsUpdateMode(true);
                setSelectedRow(data?.row);
                setShowAddEditModal(true);
              }}
              sx={{ fontSize: 22, cursor: "pointer" }}
              color="primary"
            />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteSweepIcon
              onClick={() => {
                setSelectedRow(data?.row);
                setShowDeleteConfirmModal(true);
              }}
              sx={{ fontSize: 22, cursor: "pointer" }}
              color="error"
            />
          </Tooltip>
        </Box>
      ),
    },
  ];

  const handlePagination = (values: { page: number; pageSize: number }) => {
    const offsetValue = values.page * values.pageSize;
    setTableOptions((prev) => ({
      ...prev,
      skip: offsetValue,
      take: values.pageSize,
    }));
  };

  const handleLastPage = () => {
    const totalPages = Math.ceil(
      (tableOptions.totalRowCount ?? 0) / (tableOptions.take ?? 10),
    );
    const lastPage = totalPages > 0 ? totalPages - 1 : 0;
    handlePagination({ page: lastPage, pageSize: tableOptions.take });
  };

  const handleFirstPage = () => {
    handlePagination({ page: 0, pageSize: tableOptions.take });
  };

  const handleModalClose = () => {
    setShowAddEditModal(false);
    setSelectedRow(null);
    setIsUpdateMode(false);
  };

  const handleCloseDelete = () => {
    setShowDeleteConfirmModal(false);
    setSelectedRow(null);
  };

  const handleViewClose = () => {
    setShowViewModal(false);
    setViewData(null);
  };

  const handleAddClick = () => {
    setIsUpdateMode(false);
    setSelectedRow(null);
    setShowAddEditModal(true);
  };

  const handleSaveAnnouncement = async (values: {
    title: string;
    description: string;
  }) => {
    try {
      const announcementId = selectedRow?.id;

      if (isUpdateMode && announcementId != null) {
        const response = await updateAnnouncement({
          id: announcementId,
          title: values.title,
          description: values.description,
        });

        if (response?.result) {
          setAnnouncements((prev) =>
            prev.map((item) =>
              item.id === announcementId
                ? {
                    ...item,
                    title: values.title,
                    description: values.description,
                  }
                : item,
            ),
          );
          snackActions.success(
            response?.message ?? "Announcement updated successfully",
          );
          handleModalClose();
          return true;
        }

        snackActions.error(response?.message ?? "Something went wrong!");
        return false;
      }

      const response = await createAnnounce({
        title: values.title,
        description: values.description,
      });

      if (response?.result) {
        snackActions.success(
          response?.message ?? "Announcement created successfully",
        );
        handleModalClose();
        // Refetch announcements
        const data = await getAnnouncementData();
        setAnnouncements(data?.result || []);
        setTableOptions((prev) => ({
          ...prev,
          totalRowCount: data?.result?.length || 0,
        }));
        return true;
      } else {
        snackActions.error(response?.message ?? "Something went wrong!");
        return false;
      }
    } catch (error) {
      snackActions.error(
        isUpdateMode
          ? "Failed to update announcement"
          : "Failed to create announcement",
      );
      return false;
    }
  };

  const handleDeleteAnnouncement = async () => {
    const announcementId = selectedRow?.id;

    if (announcementId == null) {
      return;
    }

    try {
      const response = await deleteAnnouncement(announcementId);

      if (response?.result) {
        setAnnouncements((prev) =>
          prev.filter((item) => item.id !== announcementId),
        );
        setTableOptions((prev) => ({
          ...prev,
          totalRowCount: Math.max((prev.totalRowCount ?? 1) - 1, 0),
        }));
        snackActions.success(
          response?.message ?? "Announcement deleted successfully",
        );
        handleCloseDelete();
      } else {
        snackActions.error(response?.message ?? "Something went wrong!");
      }
    } catch (error) {
      snackActions.error("Failed to delete announcement");
    }
  };

  return {
    announcements,
    tableOptions,
    tableData,
    columns,
    selectedRow,
    isUpdateMode,
    showAddEditModal,
    showDeleteConfirmModal,
    showViewModal,
    viewData,
    loading: isGettingListData,
    isCreatingAnnouncement,
    isDeletingAnnouncement,
    setSelectedRow,
    setShowAddEditModal,
    setShowDeleteConfirmModal,
    setShowViewModal,
    setViewData,
    handlePagination,
    handleLastPage,
    handleFirstPage,
    handleModalClose,
    handleCloseDelete,
    handleViewClose,
    handleAddClick,
    handleSaveAnnouncement,
    handleDeleteAnnouncement,
  };
};
